// Flash-message shape adapter for PageSkeleton's header messages.
//
// Two producers feed the header's <Messages> widget:
// - tempurpedic page renders build the site-local `Message` shape
//   (server/cms/templates.py) via chrome._build_messages;
// - the shared Track-B document templates (tsicommon) carry the shared
//   `MessageItem` shape (tsicommon/cms/documents/chrome.py) on their
//   PageChrome.
// The in-tree SiteHeader's <Messages> consumes only the tempurpedic shape, so
// PageSkeleton adapts here instead of forcing every Track-B wrapper to.
import type { templates } from "@reactivated";

// The tempurpedic flash-message shape (server/cms/templates.py `Message`).
export type ITPMessage =
    templates.UtilityPageTemplate["page_chrome"]["messages"][number];

// The shared tsicommon flash-message shape (`MessageItem`), as carried by
// every Track-B document template's page_chrome. Derived from the basket
// document (all the shared documents embed the same PageChrome) so it stays
// in lockstep with codegen.
export type ISharedMessageItem =
    templates.BasketDocumentTemplate["page_chrome"]["messages"][number];

export type IHeaderMessages =
    | readonly ITPMessage[]
    | readonly ISharedMessageItem[];

// Mirror Django's html escape (&, <, >, ", ') -- see below for why.
const escapeHtml = (text: string): string =>
    text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#x27;");

/**
 * Adapt either flash-message shape to the tempurpedic `Message` shape the
 * header's <Messages> consumes.
 *
 * <Messages> injects `message` as HTML, so both shapes honor the `is_html`
 * contract: only messages explicitly mark_safe'd server-side pass through as
 * HTML; plain messages are escaped first (visually identical for real
 * messages, and markup in e.g. a product title or a URL segment can't inject).
 */
export const adaptHeaderMessages = (
    messages: IHeaderMessages,
): ITPMessage[] => {
    const items: readonly (ITPMessage | ISharedMessageItem)[] = messages;
    return items.map((m) =>
        "message" in m
            ? {
                  ...m,
                  message: m.is_html ? m.message : escapeHtml(m.message),
              }
            : {
                  level: m.level,
                  message: m.is_html ? m.text : escapeHtml(m.text),
                  tags: m.tags,
                  level_tag: m.level_tag,
                  extra_tags: m.extra_tags,
                  is_html: m.is_html,
              },
    );
};
